import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminPatrolAnchorMapping } from "../../../../../http/patrol/types/patrolAnchorMappings";
import { usePatrolAnchorMappingsApi } from "../../../../../http/patrol/usePatrolAnchorMappings";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import AnchorMappingForm from "./AnchorMappingForm";
import CreateAnchorMappingModalDialogButton from "./CreateAnchorMappingModalDialogButton";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const AnchorMappings: FC<Props> = ({ active }) => {
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "anchorMappings" });
  const aborter = useRef(new AbortController());
  const patrolAnchorMappingsApi = usePatrolAnchorMappingsApi();
  const { setActions, setTitle } = useSearchContext();

  const [itemToEdit, setItemToEdit] = useState<AdminPatrolAnchorMapping>();
  const [itemToDelete, setItemToDelete] = useState<AdminPatrolAnchorMapping>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "anchorMappings");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = patrolAnchorMappingsApi.useAdminPatrolAnchorMappings(
    patrolSystemId!,
    query,
    aborter.current.signal,
  );

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.patrolAnchorMappings.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.patrolAnchorMappings.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateAnchorMappingModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminPatrolAnchorMapping>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.anchorPoints.singular}</th>
            <th align="left">{i18n.translation.roofSubstructures.singular}</th>
            <th align="left">{i18n.translation.materials.singular}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{item.item?.name + (item.extraItem ? `+${item.extraItem.code}` : "")}</td>
            <td>{item.roofSubstructure?.name}</td>
            <td>{item.material?.name}</td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.patrolAnchorMappings.edit}>
          <FlexContent direction="row">
            <AnchorMappingForm anchorMapping={itemToEdit} onSave={() => setItemToEdit(undefined)} />
          </FlexContent>
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.patrolAnchorMappings.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                patrolAnchorMappingsApi
                  .deleteAdminPatrolAnchorMapping(patrolSystemId!, itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.patrolAnchorMappings.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default AnchorMappings;
